// ── The three people lanes on a control — one vocabulary, one eligibility rule ────────────────
//
// A control carries three separate people-facts, and the whole point is that they stay separate:
//
//   assignee    → `cts_assignments.assignee_user_id`        — who is working it, right now, through
//                                                             a lifecycle. A PROCESS.
//   responsible → `cts_control_state.responsible_user_id`   — who performs this control. A PROPERTY.
//   accountable → `cts_control_state.accountable_user_id`   — who approves it. A PROPERTY.
//
// ⚠ THREE LANES, NEVER COUPLED. Setting one does not set another, and no action does two at once.
// A convenience that quietly wrote a second lane would collapse a distinction the schema holds
// deliberately — and a Responsible nobody chose is worse than none.
//
// ⚠ THE LABELS LIVE HERE AND NOWHERE ELSE. No component spells these words itself. The rendered
// vocabulary changed once already (Owner → Responsible); a codebase with the word in twelve files
// renames eleven of them.
//
// ⓘ The API contract field names (`owner_name`, `approver_name`) are deliberately NOT renamed with
// the labels. Those are identifiers, not copy — the space form is copy, the underscore form is an
// identifier (D-TERMINOLOGY-SWEEP-GREP-EXHAUSTIVE). Renaming them is a separate scoped change
// (`B-RACI-CONTRACT-FIELD-RENAME`), not a side effect of relabelling a card.

export const CTS_PEOPLE_LANES = ['assignee', 'responsible', 'accountable'] as const;
export type CtsPeopleLane = typeof CTS_PEOPLE_LANES[number];

/** The ONE label map. `accountable` renders as "Approver" — the lane key is the column, not the word. */
export const CTS_PEOPLE_LABEL: Record<CtsPeopleLane, string> = {
  assignee: 'Assignee',
  responsible: 'Responsible',
  accountable: 'Approver',
};

/** Helper text, MI-provided and used verbatim. */
export const CTS_PEOPLE_HELP: Record<CtsPeopleLane, string> = {
  assignee: 'Currently assigned to work this control',
  responsible: 'Person responsible for performing this control',
  accountable: 'Person accountable for approving the control.',
};

/**
 * The longer explanation behind each lane's information icon.
 *
 * ⚠ EACH ONE SAYS WHAT THE LANE IS *AND* HOW IT DIFFERS FROM ITS NEIGHBOUR. A definition that does
 * not distinguish is the reason these three were confused in the first place — and the Approver
 * text states plainly that holding two lanes is shown, not prevented, because the product does not
 * enforce it and the tooltip must not imply otherwise.
 */
export const CTS_PEOPLE_DETAIL: Record<CtsPeopleLane, string> = {
  assignee:
    'Who is doing the work on this control right now, tracked through an approval lifecycle '
    + '(assigned → in progress → submitted → approved). This changes hands as work moves. '
    + 'Different from Responsible, which is a standing duty rather than a current piece of work.',
  responsible:
    'Who performs this control as an ongoing duty — the standing owner of the work, whether or not '
    + 'anything is assigned today. Different from the Assignee, who is whoever currently holds a '
    + 'specific piece of work on it.',
  accountable:
    'Who approves this control and answers for it. Different from Responsible, who carries it out. '
    + 'One person may hold both — the control shows you when that happens, but does not prevent it.',
};

// ── Eligibility ───────────────────────────────────────────────────────────────────────────────
//
// ⚠ STATED AS EXCLUSIONS, NOT ALLOW-LISTS. An allow-list rots: every role added later defaults to
// ineligible, silently, and the symptom is a colleague who cannot be chosen with nothing in any log
// to say why. An exclusion list fails the other way — a new role is eligible until someone decides
// otherwise — which is the failure that announces itself.
//
// ⚠ ROLELESS IS INELIGIBLE EVERYWHERE, as a consequence rather than a special case: the rule is
// "holds at least one role this lane does not exclude", which the roleless cannot satisfy.
//
// Verified against the seeded role authority on a V1051 database (2026-09-14) — eight roles:
//   analyst · api_service · auditor · executive_viewer · member · risk_manager · super_admin ·
//   vendor_portal

/** Not human tenant users. A service account cannot perform, approve, or be assigned work. */
const NON_HUMAN_ROLES = ['vendor_portal', 'api_service'] as const;

/**
 * Excluded from EVERY lane.
 *
 * ⚠ `auditor` is here for INDEPENDENCE, not merely for being read-only: an auditor who performed,
 * approved, or was assigned a control cannot then independently audit it. That argument gets
 * stronger for the accountable lane, not weaker — which is why it is excluded from all three rather
 * than treated as a view-only case (PE ruling, 2026-09-14; reversible in this one place).
 */
const ALWAYS_EXCLUDED = [...NON_HUMAN_ROLES, 'auditor'] as const;

/**
 * ⚠ THE LANES DIFFER IN EXACTLY ONE ROLE, AND THE SHAPE BELOW SAYS SO.
 *
 * `executive_viewer` is read-only, so it cannot be given work to do — it is excluded from
 * `assignee` and `responsible`, both of which are about performing. But **approving is not
 * performing**: an executive being accountable for a control other people carry out is an ordinary
 * governance shape, and read-only-ness does not disqualify it. So `accountable` admits it.
 */
export const CTS_LANE_EXCLUDED_ROLES: Record<CtsPeopleLane, readonly string[]> = {
  assignee: [...ALWAYS_EXCLUDED, 'executive_viewer'],
  responsible: [...ALWAYS_EXCLUDED, 'executive_viewer'],
  accountable: [...ALWAYS_EXCLUDED],
};

/**
 * Eligible ⇔ the user holds at least one live role this lane does not exclude.
 *
 * ⚠ CLIENT-SIDE THIS IS UX ONLY. A picker calling this hides names; it does not refuse one. The
 * control is the api running the same rule against the user's TENANT and their LIVE (unexpired)
 * role assignments — facts a client cannot be trusted to report, and a crafted request does not
 * pass through the picker at all.
 */
export function ctsLaneEligible(
  lane: CtsPeopleLane, roles: readonly string[] | null | undefined,
): boolean {
  if (!roles || roles.length === 0) return false;
  const excluded = CTS_LANE_EXCLUDED_ROLES[lane];
  return roles.some((r) => !excluded.includes(r));
}
